import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { checkSession, login as loginRequest, logout as logoutRequest } from '@components/auth';
import { clearSessionId, getSessionId, setSessionId } from '@api/session-storage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  const refreshSession = useCallback(async () => {
    if (!getSessionId()) {
      setSession(null);
      setIsCheckingSession(false);
      return null;
    }

    try {
      setIsCheckingSession(true);
      const activeSession = await checkSession();
      setSession(activeSession);
      return activeSession;
    } catch (error) {
      clearSessionId();
      setSession(null);
      throw error;
    } finally {
      setIsCheckingSession(false);
    }
  }, []);

  useEffect(() => {
    refreshSession().catch(() => {
      // Invalid persisted sessions are handled by clearing localStorage above.
    });
  }, [refreshSession]);

  const login = useCallback(async ({ uuid, code }) => {
    const nextSession = await loginRequest(uuid, code);
    setSessionId(nextSession.code);
    setSession(nextSession);
    return nextSession;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (getSessionId()) {
        await logoutRequest();
      }
    } finally {
      clearSessionId();
      setSession(null);
    }
  }, []);

  const currentUser = session?.user || null;
  const isAdmin = Boolean(currentUser?.admin);

  const value = useMemo(
    () => ({
      session,
      currentUser,
      isAuthenticated: Boolean(session),
      isCheckingSession,
      isAdmin,
      login,
      logout,
      refreshSession,
    }),
    [session, currentUser, isCheckingSession, isAdmin, login, logout, refreshSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return value;
};
